import {
    IHierarchyMaintenancePeriod,
    IHierarchyNodeProvider,
    IHierarchyRelationship,
    IHierarchyStructure, IHierarchyStructureLevel, IHierarchyStructureSalesArea,
    IHierarchyStructureSaveOptions,
    IHierarchyStructureValidateOptions, INode
} from "../PA.MD.Core/PA.MD.IF.Hierarchy";
import * as _ from "lodash"
import {PAdbContext} from "../PA.Core/PAdbContext";
import {HierarchyHarness} from "./HierarchyHarness";
import {IMessageProvider, IProgressProvider, MessageType} from "../PA.Core/PA.Core.IF.Basic";
import {ProgressProvider} from "../PA.Core/Basic/Progress";
import {MessageProvider} from "../PA.Core/Basic/Messages";
import {
    WB_CustomerHierarchyLevelRow,
    WB_CustomerHierarchyRow, WB_CustomerHierarchySalesAreaRow,
    WB_CustomerRelationshipRow
} from "../PA.Core/PAdbSchemaRows";
import {CustomerRelation} from "./CustomerRelation";
import {CustomerNodeProvider} from "./CustomerNodeProvider";
import {
    esWB_Customer,
    esWB_Customer_Hierarchy, esWB_Customer_Hierarchy_Level, esWB_Customer_Hierarchy_SalesArea,
    esWB_Customer_Relationship,
    esWB_Hierarchy
} from "../PA.Core/PAdbSchema";
import {CustomerLevel} from "./CustomerLevel";
import {DateUtils} from "../PA.Core/Basic/Dates";
import {CustomerSalesArea} from "./CustomerSalesArea";
import {HierarchyUpdateUtils} from "./HierarchyUpdateUtils";
import {ErrorMessage_restate, ExceptionMessage_restate} from "../PA.Core/Basic/MessageUtils";


export class CustomerHierarchyStructure
    implements IHierarchyStructure
{

    private _dbContext: PAdbContext
    private _wbHierarchyId: number
    private _harness: HierarchyHarness
    private _loadMessageProvider: MessageProvider
    private _loadProgressProvider: ProgressProvider
    private _validateMessageProvider: MessageProvider
    private _saveProgressProvider: ProgressProvider
    private _saveMessageProvider: MessageProvider
    private _deleteMessageProvider: MessageProvider
    private _nodes: IHierarchyNodeProvider
    private _deletedNodes: INode[]
    private _relations: IHierarchyRelationship[]
    private _bHasEffectiveDates: boolean
    private _bExcludeManualRelationships: boolean
    private _bHasLevels: boolean
    private _levels: IHierarchyStructureLevel[]
    private _bHasSalesAreas: boolean
    private _salesAreas: IHierarchyStructureSalesArea[]
    private _bHasMaintenancePeriods: boolean
    private _maintenancePeriods: IHierarchyMaintenancePeriod[]
    private _onLoadComplete: Promise<void>
    private _bLoaded: boolean
    private _bSaved: boolean
    private _bDeleted: boolean
    private _status: string

    private wbCustHRow: WB_CustomerHierarchyRow

    constructor(wbHierarchyId: number, options?: { dbContext?: PAdbContext, harness?: HierarchyHarness, nodes?: IHierarchyNodeProvider, bExcludeManualRelationships?: boolean }) {
        this._dbContext = options?.dbContext
        this._wbHierarchyId = wbHierarchyId
        this._harness = options?.harness
        this._loadProgressProvider = new ProgressProvider("Loading")
        this._loadMessageProvider = new MessageProvider()
        this._validateMessageProvider = new MessageProvider()
        this._saveProgressProvider = new ProgressProvider("Saving")
        this._saveMessageProvider = new MessageProvider()
        this._deleteMessageProvider = new MessageProvider()
        this._nodes = options?.nodes
        this._relations = []
        this._bHasEffectiveDates = true
        this._bExcludeManualRelationships = !!options?.bExcludeManualRelationships
        this._bLoaded = false
        this._onLoadComplete = undefined
        this.wbCustHRow = undefined
        this._bSaved = false
        this._bDeleted = false
        this._bHasLevels = true
        this._levels = []
        this._bHasSalesAreas = true
        this._salesAreas = []
        this._deletedNodes = []
        this._bHasMaintenancePeriods = true
        this._maintenancePeriods = []
        this._status = ""
    }

    get dbContext(): PAdbContext {
        return this._dbContext
    }

    get wbHierarchyId(): number {
        return this._harness.WB_HierarchyRow.cif.WB_HIERARCHY_ID
    }

    get entityDimension(): string {
        return this._harness.WB_HierarchyRow.cif.ENTITY_DIMENSION
    }

    get wbId(): number {
        return this._harness.WB_HierarchyRow.cif.TARGET_WB_ID
    }

    get id_ui(): string {
        return this._harness.WB_HierarchyRow.cif.HIERARCHY_ID
    }

    get name(): string {
        return this._harness.WB_HierarchyRow.cif.HIERARCHY_NAME
    }

    get bHasClassificationNodes(): boolean {
        return this._harness.WB_HierarchyRow.cif.HIERARCHY_WORKFLOW_MODE == "MasterData"
    }

    get bHasEnterpriseEntities(): boolean {
        return this._harness.WB_HierarchyRow.bIncludesEnterpriseNodes
    }

    get bIsStatic(): boolean {
        return this._harness.WB_HierarchyRow.cif.RELATIONSHIP_TIME_POLICY=="Static"
    }

    get LoadMessageProvider(): IMessageProvider {
        return this._loadMessageProvider
    }

    get LoadProgressProvider(): IProgressProvider {
        return this._loadProgressProvider
    }

    get Nodes(): IHierarchyNodeProvider {
        return this._nodes
    }

    get Relations(): IHierarchyRelationship[] {
        return this._relations
    }

    get bHasEffectiveDates(): boolean {
        return this._bHasEffectiveDates
    }

    get bHasLevels(): boolean {
        return this._bHasLevels
    }

    get Levels(): IHierarchyStructureLevel[] {
        return this._levels
    }

    get bHasSalesAreas(): boolean { return this._bHasSalesAreas }
    get SalesAreas(): IHierarchyStructureSalesArea[] { return this._salesAreas }


    get bHasMaintenancePeriods(): boolean {
        return this._bHasMaintenancePeriods
    }

    get MaintenancePeriods(): IHierarchyMaintenancePeriod[] {
        return this._maintenancePeriods
    }

    get bHasStatus(): boolean { return true }

    get Status(): string { return this._status }

    setStatus(statusId: string): void {
        this._status = statusId || ""
    }

    get bHasDeletedNodes(): boolean { return true }

    get DeletedNodes(): INode[] { return this._deletedNodes; }



    get bLoaded(): boolean {
        return this._bLoaded
    }

    OnLoadComplete(): Promise<void> {
        const self = this

        if (self.bLoaded)
            return Promise.resolve()

        if (!_.isUndefined(self._onLoadComplete))
            return self._onLoadComplete

        if (_.isEmpty(self._dbContext))
            self._dbContext = new PAdbContext()


        let rv = self._onLoadComplete = (async () => {
            //--
            try {
                self.LoadProgressProvider.start()
                self.LoadMessageProvider.clearMessages()

                if (_.isEmpty(self._harness)) {
                    self._harness = await HierarchyHarness.findHarness(self._wbHierarchyId, {dbContext: self._dbContext})
                    if (_.isEmpty(self._harness)) {
                        // Unable to Load Harness
                        self._loadMessageProvider.addTextMessages("NoHarness", MessageType.Error, "Unable to Load Hierarchy Harness")
                        return
                    }
                }

                self._status = self._harness.WB_HierarchyRow.cif.HIERARCHY_STATUS

                let mps = _.filter(self._harness.WB_HierarchyRow.WB_HierarchyPeriodRows, (a) => (a.isActive && (a.cif.INIT_IND != '1')))
                self._maintenancePeriods = _.map(mps, (a) => { return {
                    periodId: a.cif.WB_HIERARCHYMPR_ID,
                    periodName: a.cif.PERIOD_NAME,
                    active: a.cif.ACTIVE_IND == '1',
                    startDt: DateUtils.convertFromSAPBW(a.cif.PERIOD_START_DT),
                    endDt: DateUtils.convertFromSAPBW(a.cif.PERIOD_END_DT)
                }})

                if (_.isEmpty(self._nodes))
                    self._nodes = new CustomerNodeProvider(self._dbContext, self._harness.WB_HierarchyRow.cif.WB_MDSET_ID)

                if(!self._nodes.bLoaded)
                    await self._nodes.OnLoadComplete()

                // Load Customers if it's been processed.
                let wbCustHId = self._harness.WB_HierarchyRow.cif.TARGET_WB_ID

                if (wbCustHId > 0) {
                    let ch: WB_CustomerHierarchyRow = <WB_CustomerHierarchyRow>await self.dbContext.find(esWB_Customer_Hierarchy, wbCustHId)
                    if (_.isEmpty(ch) || !ch.isActive) {
                        self._loadMessageProvider.addTextMessages("MissingHierarchy", MessageType.Error, `Unable to Load Customer Hierarchy ${wbCustHId}`)
                        return
                    }
                    self.wbCustHRow = ch

                    // Read in Sales Areas
                    let custSARows = <WB_CustomerHierarchySalesAreaRow[]> await self.dbContext.findAll(esWB_Customer_Hierarchy_SalesArea, {
                        where: {
                            DELETED_IND: {neq: '1'},
                            WB_CUSTH_ID: wbCustHId
                        }
                    })
                    for(let saRow of custSARows){
                        if(!saRow.isActive)
                            continue

                        let sa = new CustomerSalesArea(this, {saRow: saRow})
                        this._salesAreas.push(sa)
                    }

                    // Read in existing Hierarchy rows
                    let dctNodeWbIds: { [wbId: number]: number } = {}
                    let relCond: any = {
                        WB_CUSTH_ID: wbCustHId,
                        DELETED_IND: {neq: '1'}
                    }
                    if(this._bExcludeManualRelationships)
                        relCond.MANUAL_IND = {neq: '1'}

                    let relRows = <WB_CustomerRelationshipRow[]>await self.dbContext.findAll(esWB_Customer_Relationship, {
                        where: relCond,
                    })

                    // Populate Nodes first
                    for (let aRelRow of relRows) {
                        // Add Parent and Children to Node Dictionary
                        let wbIdChild = aRelRow.cif.WB_CUST_ID
                        let wbIdPrnt = aRelRow.cif.WB_CUST_ID_PRNT

                        if ((wbIdChild > 0) && (!(wbIdChild in dctNodeWbIds)))
                            dctNodeWbIds[wbIdChild] = wbIdChild
                        if ((wbIdPrnt > 0) && (!(wbIdPrnt in dctNodeWbIds)))
                            dctNodeWbIds[wbIdPrnt] = wbIdPrnt
                    }

                    await self.Nodes.findNodesByWbIds(esWB_Customer, _.values(dctNodeWbIds))

                    // Add Relationships
                    let arrNoNode: number[] = []
                    for (let aRelRow of relRows) {
                        let cr = new CustomerRelation(self, {relRow: aRelRow})
                        if (_.isEmpty(cr.child)) {
                            cr.Delete()
                            arrNoNode.push(_.isEmpty(cr.child) ? aRelRow.cif.WB_CUST_ID : aRelRow.cif.WB_CUST_ID_PRNT)
                        }
                        self._relations.push(cr)
                    }


                    let lvlRows = <WB_CustomerHierarchyLevelRow[]>await self.dbContext.findAll(esWB_Customer_Hierarchy_Level, {
                        where: {
                            WB_CUSTH_ID: wbCustHId,
                            DELETED_IND: {neq: '1'}
                        }
                    })

                    // Add Levels
                    for (let aLevelRow of lvlRows) {
                        let hlr = this._harness.getLevelById(aLevelRow.cif.LEVEL_ID)
                        let chl = new CustomerLevel(self, {lvlRow: aLevelRow, hierLvlRow: hlr})
                        self._levels.push(chl)
                    }

                    self._deletedNodes = await self._harness.getDeletedNodesFromView(self._nodes);

                    if (arrNoNode.length) {
                        self.LoadMessageProvider.addMessage(
                            {
                                code: 'NoCustomer',
                                type: MessageType.Warning,
                                title: "Can't find nodes for some customers' wbIds",
                                description: 'Below Ids not in customer tables: \n' + _.join(arrNoNode, '\n'),
                                subtitle: "",
                                count: 0
                            })
                    }

                }  // Has WB_CustH wbId

                self._bLoaded = true
                self.LoadProgressProvider.finish()
            } catch (err) {
                ErrorMessage_restate(self._loadMessageProvider,true, "Exception", `Exception when loading Customer Hierarchy ${self.wbId}`)
                ExceptionMessage_restate(self._loadMessageProvider,err)
                self.LoadProgressProvider.exception(err)
            }

        })();

        return rv
    }


    get ValidateMessageProvider(): MessageProvider {
        return this._validateMessageProvider
    }

    async Validate(options?: IHierarchyStructureValidateOptions): Promise<void> {
        this._validateMessageProvider.clearMessages()
    }

    get SaveMessageProvider(): MessageProvider {
        return this._saveMessageProvider
    }

    get SaveProgressProvider(): ProgressProvider {
        return this._saveProgressProvider
    }

    async Save(options?: IHierarchyStructureSaveOptions): Promise<void> {
        const self = this

        let bSaveHarnessRow = false
        self._bSaved = false
        self._saveMessageProvider.clearMessages()
        self._saveProgressProvider.start()
        // Create Structure if Missing, Save
        try {
            if (_.isEmpty(self.wbCustHRow)) {
                bSaveHarnessRow = true
                self.wbCustHRow = <WB_CustomerHierarchyRow>await self.dbContext.createRecordWithKey(esWB_Customer_Hierarchy)
                self.wbCustHRow.setDefaults()
                self._harness.WB_HierarchyRow.cif.TARGET_WB_ID = self.wbCustHRow.cif.WB_CUSTH_ID
            }
            self.wbCustHRow.cif.WB_MDSET_ID = self._harness.WB_HierarchyRow.cif.WB_MDSET_ID
            self.wbCustHRow.cif.WB_CUST_HIER_ID = self._harness.WB_HierarchyRow.cif.HIERARCHY_ID
            self.wbCustHRow.cif.WB_CUST_HIER_NAME = self._harness.WB_HierarchyRow.cif.HIERARCHY_NAME
            self.wbCustHRow.cif.SOURCE_ID = self._harness.WB_HierarchyRow.cif.SOURCE_ID

            if(self._harness.WB_HierarchyRow.cif.HIERARCHY_STATUS != self._status){
                self._harness.WB_HierarchyRow.cif.HIERARCHY_STATUS = self._status
                bSaveHarnessRow = true
            }

            // Save Header first, if it's new, save FK too
            self.dbContext.BatchStart()
            await self.dbContext.SaveBatch(esWB_Customer_Hierarchy, self.wbCustHRow)

            if (bSaveHarnessRow)
                await self.dbContext.SaveBatch(esWB_Hierarchy, self._harness.WB_HierarchyRow)

            await self.dbContext.BatchComplete()

            // Call harness SA sync function
            await HierarchyUpdateUtils.synchronizeSalesAreas(self, self._harness)

            // Validate
            if (!_.isEmpty(options) && ('bSkipValidate' in options) && !(options.bSkipValidate)) {
                let validateOpts = options?.validateOptions || {}
                await self.Validate(validateOpts)
                // copy messages to Save Provider
                self._saveMessageProvider.addMessages(self._validateMessageProvider.Messages)
                if (self._validateMessageProvider.countMessages(MessageType.Error) > 0) {
                    self._saveMessageProvider.addTextMessages("Validate", MessageType.Error, ["Failed Validations"])
                    self._saveProgressProvider.finish()
                    self._bSaved = false
                    return
                }
            }

            // Save Relationships
            self.dbContext.BatchStart()
            for (let aRel of self.Relations) {
                await aRel.SaveBatch()
            }

            // Save Levels
            for (let aLevel of self.Levels) {
                await aLevel.SaveBatch()
            }

            // Save Sales Areas
            for(let aSalesArea of self.SalesAreas)
                await aSalesArea.SaveBatch()

            await self.dbContext.BatchComplete()


            self._bSaved = true
            self._saveProgressProvider.finish()
        } catch (e) {
            self._bSaved = false
            self._saveMessageProvider.addTextMessages("Save", MessageType.Error, e)
            self._saveProgressProvider.exception(e)
        }


    }

    get bSaved(): boolean {
        return this._bSaved
    }

    get DeleteMessageProvider(): IMessageProvider {
        return this._deleteMessageProvider
    }

    get bDeleted(): boolean {
        return this._bDeleted
    }

    async Delete(): Promise<void> {
        const self = this
        let mp = this._deleteMessageProvider
        mp.clearMessages()

        if(!this._bLoaded){
            ErrorMessage_restate(mp, true,  "HSNotLoaded", "Unable to Delete Hierarchy Structure that isn't Loaded")
            return
        }

        // No security guard condition
        self.wbCustHRow.markDeleted()
        _.forEach(self.Relations, (rel) => {
            if(rel.isActive)
                rel.Delete()
        })

        await self.Save({bSkipValidate: true})
        self._bDeleted = true
    }


    async createRelation(): Promise<IHierarchyRelationship> {
        const self = this

        let cr = new CustomerRelation(self, {})
        self._relations.push(cr)
        return cr

    }

    async createLevel(levelId?: string): Promise<IHierarchyStructureLevel> {
        const self = this

        let hlr = this._harness.getLevelById(levelId)

        let chl = new CustomerLevel(self, {hierLvlRow: hlr})
        self._levels.push(chl)
        return chl
    }

    async createSalesArea(wbSalesAreaId?: number): Promise<IHierarchyStructureSalesArea> {
        const self = this

        let csa = new CustomerSalesArea(self, {})
        if(_.isNumber(wbSalesAreaId))
            csa.setWbSalesAreaId(wbSalesAreaId)
        self._salesAreas.push(csa)
        return csa
    }



}